var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
import { useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Button, Drawer, IconButton, Menu, MenuItem, Radio, TextField } from "@mui/material";
import { styled } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import { IApplicationsStatus } from "@/types/applications";
import { EDifferingFields, EFieldMatch, EIdentityMethod, EMatrixError, EMatrixReason, cleanConditions, ruleSentence, validateRule, } from "./approvalMatrix";
import { Help, Muted, Notice, OptionCard, OptionTitle, Overline, Segment, SegmentGroup, } from "./approvalStyles";
var DECISIONS = [
    IApplicationsStatus.ACCEPTED,
    IApplicationsStatus.PENDING,
    IApplicationsStatus.REJECTED,
];
/** The errors that no later choice in the editor can fix, shown as soon as they appear. */
var NEVER_ALLOWED = [
    EMatrixError.ACCEPTS_MANUAL_ENTRY,
    EMatrixError.ACCEPTS_ALREADY_ENROLLED,
    EMatrixError.ACCEPTS_WITHOUT_VOTER,
    EMatrixError.OTHERWISE_ACCEPTS,
];
var Panel = styled("div")({
    display: "flex",
    flexDirection: "column",
    width: "min(720px, 100vw)",
    height: "100%",
});
var Header = styled("div")({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "16px 24px",
    borderBottom: "1px solid #E3E7EF",
});
var Body = styled("div")({
    flexGrow: 1,
    overflowY: "auto",
    padding: "24px",
});
var Footer = styled("div")({
    display: "flex",
    justifyContent: "flex-end",
    gap: "12px",
    padding: "16px 24px",
    borderTop: "1px solid #E3E7EF",
});
var Heading = styled("h3")({
    margin: "24px 0 4px",
    fontSize: "16px",
    fontWeight: 600,
});
var ConditionRow = styled("div")({
    display: "flex",
    alignItems: "center",
    gap: "16px",
    padding: "12px 16px",
    marginBottom: "10px",
    border: "1px solid #E3E7EF",
    borderRadius: "10px",
});
var ConditionName = styled("span")({
    width: "164px",
    flexShrink: 0,
    fontSize: "14px",
    fontWeight: 600,
});
var keyId = function (key) {
    return key.kind === "field" ? "field:".concat(key.field) : key.kind;
};
export var ApprovalMatrixRuleDialog = function (_a) {
    var _b;
    var open = _a.open, rule = _a.rule, number = _a.number, isOtherwise = _a.isOtherwise, comparedFields = _a.comparedFields, validIds = _a.validIds, fieldLabel = _a.fieldLabel, onClose = _a.onClose;
    var t = useTranslation().t;
    var id = useId();
    var _c = useState(rule), draft = _c[0], setDraft = _c[1];
    var _d = useState(false), tried = _d[0], setTried = _d[1];
    var _e = useState(null), menuAnchor = _e[0], setMenuAnchor = _e[1];
    useEffect(function () {
        if (open) {
            setDraft(rule);
            setTried(false);
        }
    }, [open, rule]);
    var edited = {
        when: isOtherwise ? {} : cleanConditions(draft.when, comparedFields),
        then: draft.then.decision === IApplicationsStatus.ACCEPTED
            ? { decision: draft.then.decision }
            : draft.then,
    };
    var found = validateRule(edited, isOtherwise);
    var blocking = found.filter(function (error) { return NEVER_ALLOWED.includes(error); });
    var shown = tried ? found : blocking;
    var setCondition = function (change) {
        return setDraft(function (current) { return (__assign(__assign({}, current), { when: __assign(__assign({}, current.when), change) })); });
    };
    var setField = function (field, match) {
        var fields = __assign({}, draft.when.fields);
        if (match) {
            fields[field] = match;
        }
        else {
            delete fields[field];
        }
        setCondition({ fields: fields });
    };
    var apply = function () {
        setTried(true);
        if (found.length === 0) {
            onClose(edited);
        }
    };
    var title = isOtherwise
        ? t("approvalsScreen.matrix.dialog.otherwiseTitle")
        : number === null
            ? t("approvalsScreen.matrix.dialog.newTitle")
            : t("approvalsScreen.matrix.dialog.editTitle", { number: number });
    var name = function (key) {
        return key.kind === "field"
            ? fieldLabel(key.field)
            : String(t({
                identity: "approvalsScreen.matrix.dialog.identity",
                voter_found: "approvalsScreen.matrix.dialog.voterFound",
                already_enrolled: "approvalsScreen.matrix.dialog.alreadyEnrolled",
                valid_id: "approvalsScreen.matrix.dialog.validId",
                differing: "approvalsScreen.matrix.dialog.differing",
            }[key.kind]));
    };
    var all = __spreadArray([
        { kind: "identity" },
        { kind: "voter_found" },
        { kind: "already_enrolled" },
        { kind: "valid_id" },
        { kind: "differing" }
    ], comparedFields.map(function (field) { return ({ kind: "field", field: field }); }), true);
    var isSet = function (key) {
        var _a;
        return key.kind === "field"
            ? ((_a = draft.when.fields) === null || _a === void 0 ? void 0 : _a[key.field]) !== undefined
            : draft.when[key.kind] !== undefined;
    };
    var present = all.filter(isSet);
    var absent = all.filter(function (key) { return !isSet(key); });
    var add = function (key) {
        var _a;
        var _b;
        setMenuAnchor(null);
        if (key.kind === "field") {
            setField(key.field, EFieldMatch.DIFFERS);
        }
        else if (key.kind === "identity") {
            setCondition({ identity: EIdentityMethod.VERIFIED });
        }
        else if (key.kind === "valid_id") {
            setCondition({ valid_id: (_b = validIds[0]) !== null && _b !== void 0 ? _b : "" });
        }
        else if (key.kind === "differing") {
            setCondition({ differing: EDifferingFields.EXACTLY_1 });
        }
        else {
            setCondition((_a = {}, _a[key.kind] = true, _a));
        }
    };
    var remove = function (key) {
        var _a;
        return key.kind === "field"
            ? setField(key.field, undefined)
            : setCondition((_a = {}, _a[key.kind] = undefined, _a));
    };
    var segments = function (label, options, value, onChoose) { return (_jsx(SegmentGroup, { role: "group", "aria-label": label, children: options.map(function (option) { return (_jsx(Segment, { type: "button", "aria-pressed": option.value === value, onClick: function () { return onChoose(option.value); }, children: option.text }, String(option.value))); }) })); };
    var yesNo = [
        { value: true, text: String(t("approvalsScreen.matrix.dialog.yes")) },
        { value: false, text: String(t("approvalsScreen.matrix.dialog.no")) },
    ];
    var control = function (key) {
        var _a, _b, _c;
        var label = name(key);
        switch (key.kind) {
            case "identity":
                return segments(label, Object.values(EIdentityMethod).map(function (method) { return ({
                    value: method,
                    text: String(t("approvalsScreen.matrix.identity.".concat(method))),
                }); }), draft.when.identity, function (identity) { return setCondition({ identity: identity }); });
            case "voter_found":
                return segments(label, yesNo, draft.when.voter_found, function (voter_found) {
                    return setCondition({ voter_found: voter_found });
                });
            case "already_enrolled":
                return segments(label, yesNo, draft.when.already_enrolled, function (already_enrolled) {
                    return setCondition({ already_enrolled: already_enrolled });
                });
            case "valid_id":
                return (_jsx(TextField, { select: true, fullWidth: true, size: "small", slotProps: { htmlInput: { "aria-label": label } }, value: (_a = draft.when.valid_id) !== null && _a !== void 0 ? _a : "", onChange: function (event) { return setCondition({ valid_id: event.target.value }); }, children: __spreadArray(__spreadArray([], validIds, true), (draft.when.valid_id && !validIds.includes(draft.when.valid_id)
                        ? [draft.when.valid_id]
                        : []), true).map(function (validId) { return (_jsx(MenuItem, { value: validId, children: t(validId) }, validId)); }) }));
            case "differing":
                return (_jsx(TextField, { select: true, fullWidth: true, size: "small", slotProps: { htmlInput: { "aria-label": label } }, value: (_b = draft.when.differing) !== null && _b !== void 0 ? _b : "", onChange: function (event) {
                        return setCondition({
                            differing: Object.values(EDifferingFields).find(function (count) { return count === event.target.value; }),
                        });
                    }, children: Object.values(EDifferingFields).map(function (count) { return (_jsx(MenuItem, { value: count, children: t("approvalsScreen.matrix.differing.".concat(count)) }, count)); }) }));
            case "field":
                return segments(label, Object.values(EFieldMatch).map(function (match) { return ({
                    value: match,
                    text: String(t("approvalsScreen.matrix.fieldMatch.".concat(match))),
                }); }), (_c = draft.when.fields) === null || _c === void 0 ? void 0 : _c[key.field], function (match) { return setField(key.field, match); });
        }
    };
    var reasonLabel = String(t("approvalsScreen.matrix.dialog.reason"));
    return (_jsx(Drawer, { anchor: "right", open: open, onClose: function () { return onClose(null); }, 
        // The drawer is the dialog: its title names it.
        slotProps: { paper: { "aria-labelledby": "".concat(id, "-title") } }, children: _jsxs(Panel, { children: [_jsxs(Header, { children: [_jsx(Box, { component: "h2", id: "".concat(id, "-title"), sx: { margin: 0, fontSize: "22px" }, children: title }), _jsx(IconButton, { "aria-label": String(t("approvalsScreen.matrix.dialog.close")), onClick: function () { return onClose(null); }, children: _jsx(CloseIcon, {}) })] }), _jsxs(Body, { children: [_jsx(Notice, { "data-tone": "neutral", children: _jsxs("div", { children: [_jsx(Overline, { sx: { margin: "0 0 4px" }, children: t("approvalsScreen.matrix.dialog.summary") }), _jsx(Box, { sx: { fontSize: "16px" }, "data-testid": "rule-summary", children: ruleSentence(edited, isOtherwise, t, fieldLabel) })] }) }), !isOtherwise && (_jsxs(_Fragment, { children: [_jsx(Heading, { children: t("approvalsScreen.matrix.when") }), _jsx(Help, { children: t("approvalsScreen.matrix.dialog.whenHelp") }), present.map(function (key) { return (_jsxs(ConditionRow, { children: [_jsx(ConditionName, { children: name(key) }), _jsx(Box, { sx: { flexGrow: 1 }, children: control(key) }), _jsx(IconButton, { size: "small", "aria-label": String(t("approvalsScreen.matrix.dialog.remove", {
                                                condition: name(key),
                                            })), onClick: function () { return remove(key); }, children: _jsx(CloseIcon, { fontSize: "small" }) })] }, keyId(key))); }), _jsx(Button, { variant: "secondary", startIcon: _jsx(AddIcon, {}), disabled: absent.length === 0, "aria-haspopup": "menu", onClick: function (event) { return setMenuAnchor(event.currentTarget); }, children: t("approvalsScreen.matrix.dialog.addCondition") }), _jsx(Menu, { anchorEl: menuAnchor, open: menuAnchor !== null, onClose: function () { return setMenuAnchor(null); }, children: absent.map(function (key) { return (_jsx(MenuItem, { onClick: function () { return add(key); }, children: name(key) }, keyId(key))); }) })] })), _jsx(Heading, { children: isOtherwise
                                ? t("approvalsScreen.matrix.dialog.otherwiseHelp")
                                : t("approvalsScreen.matrix.then") }), _jsx(Box, { role: "radiogroup", "aria-label": String(t("approvalsScreen.matrix.dialog.decision")), sx: { display: "flex", flexDirection: "column", gap: "10px" }, children: DECISIONS.map(function (decision) { return (_jsxs(OptionCard, { "data-selected": draft.then.decision === decision, children: [_jsx(Radio, { sx: { padding: "2px" }, name: "".concat(id, "-decision"), checked: draft.then.decision === decision, onChange: function () {
                                            return setDraft(function (current) {
                                                var _a;
                                                return (__assign(__assign({}, current), { 
                                                    // The reason is kept while trying Approve, which
                                                    // has none; a rule that stops approving starts
                                                    // from the reason the last rule gives.
                                                    then: {
                                                        decision: decision,
                                                        reason: (_a = current.then.reason) !== null && _a !== void 0 ? _a : (decision !== IApplicationsStatus.ACCEPTED &&
                                                            rule.then.decision ===
                                                                IApplicationsStatus.ACCEPTED
                                                            ? EMatrixReason.NO_VOTER
                                                            : undefined),
                                                    } }));
                                            });
                                        } }), _jsxs("div", { children: [_jsx(OptionTitle, { children: t("approvalsScreen.matrix.decisions.".concat(decision)) }), _jsx(Muted, { children: t("approvalsScreen.matrix.outcomeHelp.".concat(decision)) })] })] }, decision)); }) }), draft.then.decision !== IApplicationsStatus.ACCEPTED && (_jsxs(_Fragment, { children: [_jsx(Heading, { children: reasonLabel }), _jsxs(TextField, { select: true, fullWidth: true, size: "small", slotProps: {
                                        select: { displayEmpty: true },
                                        htmlInput: { "aria-label": reasonLabel },
                                    }, value: (_b = draft.then.reason) !== null && _b !== void 0 ? _b : "", onChange: function (event) {
                                        return setDraft(function (current) { return (__assign(__assign({}, current), { then: {
                                                decision: current.then.decision,
                                                reason: Object.values(EMatrixReason).find(function (reason) { return reason === event.target.value; }),
                                            } })); });
                                    }, children: [_jsx(MenuItem, { value: "", children: "-" }), Object.values(EMatrixReason).map(function (reason) { return (_jsx(MenuItem, { value: reason, children: t("approvalsScreen.matrix.reasons.".concat(reason)) }, reason)); })] }), draft.then.reason && (_jsx(Notice, { "data-tone": "neutral", sx: { marginTop: "12px" }, children: _jsxs("div", { children: [_jsx(Overline, { sx: { margin: "0 0 4px" }, children: t("approvalsScreen.matrix.dialog.voterSees") }), t("approvalsScreen.matrix.voterText.".concat(draft.then.reason))] }) }))] })), shown.map(function (error) { return (_jsx(Notice, { "data-tone": "error", role: "alert", sx: { marginTop: "16px" }, children: t("approvalsScreen.matrix.errors.".concat(error)) }, error)); })] }), _jsxs(Footer, { children: [_jsx(Button, { variant: "cancel", onClick: function () { return onClose(null); }, children: t("common.label.cancel") }), _jsx(Button, { disabled: blocking.length > 0, onClick: apply, children: t("approvalsScreen.matrix.dialog.apply") })] })] }) }));
};
